import React from 'react'
import Helmet from 'react-helmet'

import { getCmsErrorPageStatus, PageRenderer, useCmsBlock } from '@library/cms-blocks'
import type { CmsErrorPageStatus, CmsFetcher, OnTriggerPromoCode } from '@library/cms-blocks'

import { getCmsSeo } from './seo'

const CMS_PAGE_CONTENT_TYPE = 'cms_page'

type Props = {
  pageUrl: string
  cmsFetcher: CmsFetcher
  onTriggerPromocode: OnTriggerPromoCode
  renderError?: (status: CmsErrorPageStatus) => React.ReactNode
}

// Resolves and renders any CMS page from its `pageUrl` — no per-page code.
export function DynamicCmsPage({ pageUrl, cmsFetcher, onTriggerPromocode, renderError }: Props) {
  const { data, loading, error, errorCode } = useCmsBlock(
    { contentType: CMS_PAGE_CONTENT_TYPE, pageUrl },
    cmsFetcher,
  )

  // Only on client-side nav; SSR pre-populates the store before render.
  if (loading) {
    return null
  }

  // A failed fetch maps to the matching error page (404 vs 500); a successful
  // fetch with no entry is a genuine not-found (404). Errors render here rather
  // than throwing in fetchData, so a transient CMS outage never hard-fails the
  // request or clears the persistent store (basket).
  if (error || !data) {
    const status: CmsErrorPageStatus = error ? getCmsErrorPageStatus(errorCode) : '404'

    return renderError ? <>{renderError(status)}</> : null
  }

  const seo = getCmsSeo(data, pageUrl)

  return (
    <div data-testid="dynamic-cms-page">
      <Helmet title={seo.title} link={[{ rel: 'canonical', href: seo.canonical }]}>
        <meta name="description" content={seo.description} />
        <meta name="robots" content={seo.robots} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={seo.canonical} />
        <meta property="og:image" content={seo.ogImage} />
      </Helmet>
      <PageRenderer page={data} onTriggerPromocode={onTriggerPromocode} />
    </div>
  )
}
